import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { ColumnDef } from '@tanstack/react-table';

import { useIncidentComs } from '../../../hooks/incidentComsHooks';
import type { IncidentComs } from '../../../hooks/incidentComsHooks';
import { INCIDENT } from '../../../ICSConstants';
import {
  buildContactInfoFromIcs205Com,
  getComDisplayName,
} from '../../../utils/workAssignmentUtils';
import { NULL_VALUE } from 'constants/displayConstants';
import { useMemo } from 'react';

type CommunicationsDisplayProps = {
  workGroupId?: string;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
};

const CommunicationsDisplay = ({
  workGroupId,
  medicalCommunication,
  evacuationCommunication,
  otherCommunication,
}: CommunicationsDisplayProps) => {
  const { data: incidentComs = [] } = useIncidentComs(INCIDENT);
  const workGroupComs =
    workGroupId && incidentComs.length > 0
      ? incidentComs.filter(
          (c) => c.work_group_id === workGroupId && !c.archived
        )
      : [];

  const allComsRows: { com: (typeof incidentComs)[0]; isWorkGroup: boolean }[] =
    [...workGroupComs.map((com) => ({ com, isWorkGroup: true }))];

  const comsColumns = useMemo<
    ColumnDef<{ com: IncidentComs; isWorkGroup: boolean }>[]
  >(
    () => [
      {
        id: 'name',
        header: () => 'Name / Function',
        cell: ({ row }) => {
          const { com } = row.original;
          return <>{getComDisplayName(com)}</>;
        },
        enableSorting: false,
      },
      {
        id: 'contactInfo',
        header: () => 'Contact Information',
        cell: ({ row }) =>
          buildContactInfoFromIcs205Com(row.original.com) || NULL_VALUE,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <>
      <div className="mt-3">
        <div className="txt--semibold mb-2">Communications:</div>
        <div className="mx-3">
          <PratusTable
            columns={comsColumns}
            data={allComsRows}
            getRowId={(row, i) =>
              `${row.com.coms_id}-${row.isWorkGroup ? 'wg' : 'user'}-${i}`
            }
            noDataIndication="No communications. Edit the 205 to add."
            responsive={false}
          />
        </div>
        <div className="txt--semibold">Emergency Communications:</div>
        <div className="filter-card-content-grid p-4">
          <div title="Medical">{`Medical: ${medicalCommunication || NULL_VALUE}`}</div>
          <div title="Evacuation">{`Evacuation: ${evacuationCommunication || NULL_VALUE}`}</div>
          <div title="Other">
            {`Other: ${otherCommunication || NULL_VALUE}`}
          </div>
        </div>
      </div>
    </>
  );
};

export default CommunicationsDisplay;
